import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { ModalDialogService } from '@ddv/common-components';
import {
    AggType,
    BaseConfigItem,
    ChartConfigItem,
    LabelValuePair,
    TooltipConfigItem,
    VisualizationType,
} from '@ddv/models';

import { DialogRef } from '../../../models/dialog-ref';
import { ChartDropZoneConfig } from '../../../models/drop-zone-config';
import { DropZoneId } from '../../../models/drop-zone-id';
import { getAggregationDropdownList } from '../../../utils/get-aggregation-dropdown-list';
import { isValueZone } from '../../../utils/is-value-zone';
import { ColumnBaseFormattingComponent } from '../column-base-formatting/column-base-formatting.component';
import { ColumnTooltipFormattingComponent } from '../column-tooltip-formatting/column-tooltip-formatting.component';
import { ColumnValueFormattingComponent } from '../column-value-formatting/column-value-formatting.component';

@Component({
    selector: 'app-ddv-configuration-field',
    templateUrl: './configuration-field.component.html',
    styleUrls: ['./configuration-field.component.scss'],
})
export class ConfigurationFieldComponent implements OnInit {
    @Input() zone: ChartDropZoneConfig | undefined;
    @Input() dataConfig: BaseConfigItem | ChartConfigItem | TooltipConfigItem | undefined;
    @Input() visualizationType: VisualizationType | undefined;
    @Output() removeConfigField = new EventEmitter<string>();
    @Output() configurationFieldChange = new EventEmitter<void>();

    private readonly modalService = inject(ModalDialogService);

    protected displayName: string | undefined;
    private aggregationDropdownList: LabelValuePair<AggType>[] = [];

    ngOnInit(): void {
        if (!this.dataConfig || !this.zone) {
            return console.error('Cannot initialize this component without dataConfig and zone');
        }
        this.aggregationDropdownList = getAggregationDropdownList(
            this.dataConfig.displayType,
            this.zone.id,
            this.visualizationType ?? 'HORIZONTAL_BAR_CHART',
        );
        this.displayName = this.getDisplayName(this.dataConfig);
    }

    protected showColumnFormatters(config: BaseConfigItem | ChartConfigItem | TooltipConfigItem, zoneId: DropZoneId): void {
        const dialogRef = this.getDialogRef(zoneId);
        dialogRef.componentInstance.parentData = config;

        if (isValueZone(zoneId)) {
            (dialogRef.componentInstance as ColumnValueFormattingComponent).visualizationType = this.visualizationType;
        }

        dialogRef.result.then(
            (response) => {
                if (response !== 'cancel') {
                    Object.assign(config, response);
                    this.displayName = this.getDisplayName(config);
                    this.configurationFieldChange.emit();
                }
            },
            () => {},
        );
    }

    protected onRemove(colId: string): void {
        this.removeConfigField.emit(colId);
    }

    private getDisplayName(config: BaseConfigItem | ChartConfigItem | TooltipConfigItem): string {
        const { showCustomName, customName, label } = config;

        if ('aggregationType' in config && config.aggregationType) {
            const aggregationType = this.aggregationDropdownList.find((agg) => agg.value === config.aggregationType)?.label ??
                config.aggregationType;
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            return `${aggregationType} of ${showCustomName && customName || label}`;
        }

        if (showCustomName && customName != null) {
            return customName;
        }

        return label;
    }

    private getDialogRef(zone: DropZoneId): DialogRef {
        const windowClass = 'column-formatter-popup';

        if (['axisTime', 'details', 'lslicer', 'saslicer', 'slicer'].includes(zone)) {
            return this.modalService.open<BaseConfigItem, ColumnBaseFormattingComponent>(ColumnBaseFormattingComponent, { windowClass });
        }

        if (isValueZone(zone)) {
            return this.modalService
                .open<ChartConfigItem, ColumnValueFormattingComponent>(ColumnValueFormattingComponent, { windowClass });
        }

        return this.modalService
            .open<TooltipConfigItem, ColumnTooltipFormattingComponent>(ColumnTooltipFormattingComponent, { windowClass });
    }
}
